import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import './main.css';

import { ColorSchemeScript, createTheme, MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import RelativeTime from 'dayjs/plugin/relativeTime.js';
import UTC from 'dayjs/plugin/utc.js';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { startVersionCheck } from './lib/versionCheck.tsx';
import { AuthProvider } from './models/auth.tsx';

dayjs.extend(RelativeTime);
dayjs.extend(UTC);

export const IS_PROD = import.meta.env.MODE === 'production';
export const IS_DEV = import.meta.env.MODE === 'development';

const theme = createTheme({
  colors: {
    blue: [
      '#e5f3ff',
      '#cde2ff',
      '#9ac2ff',
      '#64a0ff',
      '#3884fe',
      '#1d72fe',
      '#0063ff',
      '#0058e4',
      '#004ecd',
      '#0043b5',
    ],
  },
  primaryShade: { light: 9, dark: 7 },
  primaryColor: 'blue',
  defaultRadius: 'sm',
});

if (!IS_DEV) {
  Sentry.init({
    dsn: 'https://5595516edb788a9cfe85c9d439afb064@o4507537515741184.ingest.de.sentry.io/4508448899334224',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/bo\.(stg\.)?legalbison.com\.com\/api/,
    ],
    replaysSessionSampleRate: IS_PROD ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

createRoot(document.getElementById('root')!).render(
  <>
    <ColorSchemeScript defaultColorScheme="auto" />
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <Notifications limit={2} autoClose={10000} />
      <ModalsProvider>
        <GoogleOAuthProvider
          clientId={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID}
        >
          <AuthProvider>
            <App />
          </AuthProvider>
        </GoogleOAuthProvider>
      </ModalsProvider>
    </MantineProvider>
  </>,
);

if (!IS_DEV) startVersionCheck();

if (!IS_PROD) {
  const div = document.createElement('div');
  const span = document.createElement('span');
  div.appendChild(span);
  span.textContent = import.meta.env.MODE;
  document.body.appendChild(div);
  div.classList.add('env-warn');
}
